import { BarChart3, Clock, Hammer, Trophy, Pickaxe } from 'lucide-react'
import type { ComponentType } from 'react'
import PageHeader from '@/components/feature/PageHeader'
import ComingSoon from '@/components/feature/ComingSoon'
import serverConfig from '@/config/serverConfig'
import { useLanguage } from '@/localization'

/** Greyed-out preview rows so the page reads as an intentional skeleton. */
const LEADERBOARD_PREVIEW = [
  { rank: 1, width: 'w-32' },
  { rank: 2, width: 'w-28' },
  { rank: 3, width: 'w-36' },
  { rank: 4, width: 'w-24' },
  { rank: 5, width: 'w-32' },
]

const STAT_CARDS: { icon: ComponentType<{ size?: number; className?: string }>; label: string }[] = [
  { icon: Clock, label: 'Hours played' },
  { icon: Pickaxe, label: 'Blocks mined' },
  { icon: Hammer, label: 'Blocks placed' },
  { icon: Trophy, label: 'Advancements' },
]

function StatsPreview() {
  const { t } = useLanguage()

  return (
    <div
      className="relative mx-auto mt-2 max-w-3xl select-none"
      aria-hidden="true"
    >
      {/* faux stat cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {STAT_CARDS.map(({ icon: Icon, label }) => (
          <div
            key={label}
            className="rounded-xl border border-stone-light/60 bg-white p-4 text-center shadow-block"
          >
            <Icon size={22} className="mx-auto mb-2 text-grass-dark/50" />
            <div className="mx-auto mb-2 h-5 w-12 rounded bg-stone-light/60" />
            <p className="text-xs font-bold text-slate-deep/50">{t(label)}</p>
          </div>
        ))}
      </div>

      {/* faux leaderboard */}
      <div className="mt-6 overflow-hidden rounded-2xl border border-stone-light/60 bg-white shadow-block">
        <div className="flex items-center gap-2 border-b border-stone-light/60 bg-[#f4f7f2] px-5 py-3">
          <BarChart3 size={16} className="text-grass-dark" />
          <span className="font-pixel text-[10px] uppercase tracking-[0.2em] text-grass-deep">
            {t('Top players')}
          </span>
        </div>
        <ul>
          {LEADERBOARD_PREVIEW.map(({ rank, width }) => (
            <li
              key={rank}
              className="flex items-center gap-4 border-b border-stone-light/40 px-5 py-3 last:border-b-0"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-grass/15 font-pixel text-[10px] text-grass-deep">
                {rank}
              </span>
              <span className="h-8 w-8 shrink-0 rounded-md bg-stone-light/60" />
              <span className={`h-4 rounded bg-stone-light/60 ${width}`} />
              <span className="ml-auto h-4 w-16 rounded bg-stone-light/40" />
            </li>
          ))}
        </ul>
      </div>

      {/* soft fade so it clearly reads as a not-yet-live preview */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#f4f7f2] to-transparent" />
    </div>
  )
}

export default function StatsPage() {
  const { t } = useLanguage()

  if (serverConfig.features.statsEnabled) {
    // Live stats view would render here once enabled.
    return (
      <>
        <PageHeader title="Player Stats" subtitle="Live leaderboards and player statistics." />
        <div className="max-w-5xl mx-auto px-4 py-12 text-center text-slate-deep/60">
          <BarChart3 className="mx-auto mb-3 text-grass-dark" size={40} />
          {t('Stats are enabled — leaderboard wiring goes here.')}
        </div>
      </>
    )
  }

  return (
    <ComingSoon
      title="Player Stats"
      description="Leaderboards, playtime, blocks mined and bragging rights — once the servers are live and the numbers start rolling in, you'll find them all here."
    >
      <StatsPreview />
    </ComingSoon>
  )
}
